/**
 * LiveTestingHeadSelectField 컴포넌트
 *
 * body_select_head 타입을 위한 체크박스 리스트 UI
 * V1 BodyPartSelectorHead의 V2 버전
 */

import { memo, useCallback } from 'react';
import { QuestionResponseDto } from '../../../../api/generated/types';
import { BodySelectValue } from './LiveTestingBodySelectField';
import { Checkbox } from '../../../../shared/components/Checkbox/Checkbox';
import { useTranslations } from '../../../../../patient/hooks/use-translations';
import { normalizeTranslatedLabelForSurveyLocale } from '../../../../shared/utils/survey-locale-text';

// Head SVG 이미지 imports
import oEyeLt from '../../../../shared/assets/head/o-eye-lt.svg';
import oEyeRt from '../../../../shared/assets/head/o-eye-rt.svg';
import oHeadBack from '../../../../shared/assets/head/o-head-back.svg';
import oHeadBand from '../../../../shared/assets/head/o-head-band.svg';
import oHeadFront from '../../../../shared/assets/head/o-head-front.svg';
import oHeadSideLt from '../../../../shared/assets/head/o-head-side-lt.svg';
import oHeadSideRt from '../../../../shared/assets/head/o-head-side-rt.svg';
import oHeadTempleLt from '../../../../shared/assets/head/o-head-temple-lt.svg';
import oHeadTempleRt from '../../../../shared/assets/head/o-head-temple-rt.svg';
import oHeadTop from '../../../../shared/assets/head/o-head-top.svg';
import oHeadWhole from '../../../../shared/assets/head/o-head-whole.svg';
import oNeckBack from '../../../../shared/assets/head/o-neck-back.svg';
import oNeckLt from '../../../../shared/assets/head/o-neck-lt.svg';
import oNeckRt from '../../../../shared/assets/head/o-neck-rt.svg';
import oOther from '../../../../shared/assets/head/o-other.svg';
import oSinus from '../../../../shared/assets/head/o-sinus.svg';

// Head 이미지 맵
const headImageMap: Record<string, string> = {
  'o-head-whole': oHeadWhole,
  'o-head-top': oHeadTop,
  'o-head-front': oHeadFront,
  'o-head-back': oHeadBack,
  'o-sinus': oSinus,
  'o-head-band': oHeadBand,
  'o-head-side-rt': oHeadSideRt,
  'o-head-temple-rt': oHeadTempleRt,
  'o-eye-rt': oEyeRt,
  'o-head-side-lt': oHeadSideLt,
  'o-head-temple-lt': oHeadTempleLt,
  'o-eye-lt': oEyeLt,
  'o-neck-lt': oNeckLt,
  'o-neck-rt': oNeckRt,
  'o-neck-back': oNeckBack,
  'o-other': oOther,
};

// 기본 answerKey 순서 (V1과 동일)
const DEFAULT_HEAD_ANSWER_KEYS = [
  'o-head-whole',
  'o-head-top',
  'o-head-front',
  'o-head-back',
  'o-sinus',
  'o-head-band',
  'o-head-side-rt',
  'o-head-temple-rt',
  'o-eye-rt',
  'o-head-side-lt',
  'o-head-temple-lt',
  'o-eye-lt',
  'o-neck-lt',
  'o-neck-rt',
  'o-neck-back',
  'o-other',
];

// answerKey를 라벨로 변환
const answerKeyLabels: Record<string, string> = {
  'o-head-whole': 'Whole head',
  'o-head-top': 'Top of head',
  'o-head-front': 'Front of head',
  'o-head-back': 'Back of head',
  'o-sinus': 'Sinus',
  'o-head-band': 'Band like around head',
  'o-head-side-rt': 'Right side of head',
  'o-head-temple-rt': 'Right temple',
  'o-eye-rt': 'Right eye',
  'o-head-side-lt': 'Left side of head',
  'o-head-temple-lt': 'Left temple',
  'o-eye-lt': 'Left eye',
  'o-neck-lt': 'Left neck',
  'o-neck-rt': 'Right neck',
  'o-neck-back': 'Back of neck',
  'o-other': 'Other',
};

interface LiveTestingHeadSelectFieldProps {
  question: QuestionResponseDto;
  value: BodySelectValue;
  onChange: (value: BodySelectValue) => void;
  disabled?: boolean;
  locale?: string;
}

function fallbackLabelFromAnswerKey(answerKey: string) {
  return answerKey
    .replace(/^o-/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function LiveTestingHeadSelectField({
  question,
  value,
  onChange,
  disabled = false,
  locale = 'english',
}: LiveTestingHeadSelectFieldProps) {
  const [, { t }] = useTranslations({ locale });
  // answerKeys를 question에서 가져오거나 기본값 사용
  const answerKeys = question.answerKeys?.length
    ? question.answerKeys
    : DEFAULT_HEAD_ANSWER_KEYS;

  const selectedKeys = value?.answerKeys || [];

  // 체크박스 클릭 핸들러
  const handleClick = useCallback(
    (answerKey: string) => {
      if (disabled) return;

      const isSelected = selectedKeys.includes(answerKey);
      let newKeys: string[];

      if (isSelected) {
        // 선택 해제
        newKeys = selectedKeys.filter((k: string) => k !== answerKey);
      } else {
        // 선택 추가
        newKeys = [...selectedKeys, answerKey];
      }

      onChange({
        ...value,
        answerKeys: newKeys,
      });
    },
    [selectedKeys, value, onChange, disabled]
  );

  return (
    <div className="flex flex-col">
      {/* V1과 동일: gap-y-10 */}
      <div className="grid grid-cols-3 gap-x-2 gap-y-10 pt-2 pb-3">
        {answerKeys.map((answerKey) => {
          const img = headImageMap[answerKey];
          const isSelected = selectedKeys.includes(answerKey);
          const optionLabel = question.answerOptionsMap?.[answerKey]?.label as string | undefined;
          const normalizedOptionLabel = normalizeTranslatedLabelForSurveyLocale({
            locale,
            label: optionLabel,
            fallbackKey: '',
          });
          const translatedKey = t(answerKey, null, { fallbackValue: null });
          const label =
            (normalizedOptionLabel && normalizedOptionLabel.trim().length > 0
              ? normalizedOptionLabel
              : '') ||
            (translatedKey && translatedKey !== answerKey ? translatedKey : '') ||
            answerKeyLabels[answerKey] ||
            fallbackLabelFromAnswerKey(answerKey);

          return (
            <div key={answerKey} className="flex justify-center items-start">
              {/* V1과 동일: gap-4 */}
              <div className="flex flex-col gap-4">
                {/* 이미지 - V1과 동일하게 크기 제약 없음 */}
                <div className="flex justify-center">
                  {img && <img src={img} alt={label} />}
                </div>

                {/* 체크박스 + 라벨 - 디자인 시스템 Checkbox 사용 */}
                <div className="flex flex-col items-center gap-2">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => handleClick(answerKey)}
                  />
                  <label className="text-sm text-center text-[#222638]">
                    {label}
                  </label>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default memo(LiveTestingHeadSelectField);
